import keyBy from 'lodash/keyBy';
import { ensureHasLeadingSlash } from './utils/basePathHelpers';
import Routes from './routes';

// Runtime configuration. The sentinel strings declared in client/index.html
// are swapped by docker-entrypoint.sh at container start.
const runtimeConfig = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};
const runtimeBasePath = runtimeConfig.basePath && runtimeConfig.basePath !== 'PUBLIC_URL_REPLACE_ME'
  ? runtimeConfig.basePath
  : '';
const runtimePendoKey = runtimeConfig.pendoKey && runtimeConfig.pendoKey !== 'REACT_APP_PENDO_KEY_REPLACE_ME'
  ? runtimeConfig.pendoKey
  : '';

const Config = {
  batchProcessing: {
    maxRowsPerRequest: 50000,
  },
  defaultJobsLimit: 10,
  endpoints: {
    APP_DATA: '/api/app',
    PROVISIONS: '/api/provisions/:periodName',
    PERIODS: '/api/periods',
    CURRENT_PERIOD: '/api/periods/current',
    YEAR_START_PERIOD: '/api/periods/year-start',
    RETURN_TO_PROVISION: '/api/return-to-provision/:periodName',
    UPLOAD_TO_GTW: '/api/gtw/uploadRTPToGTW/:taxYear',
    PROVISION: '/api/provisions/:companyCode/:period',
    CALCULATE_PROVISION: '/api/commands/calculate/:companyCode/:period/:businessUnit',
    UPLOAD_BALANCE_FILE: '/api/provisions/trial-balance/:companyCode/:period?isBeginningBalance=:isBeginningBalance&companySource=:companySource',
    VALIDATE_BALANCE_FILE: '/api/provisions/validate-balance-file?isBeginningBalance=:isBeginningBalance',
    TAX_YEARS: '/api/tax-years',
    TAX_ENTRIES_COUNTRY_TAX_TYPES: '/api/tax-entries/country-tax-types/:companyCode/:period/:staticOptions',
    FETCH_TAX_ENTRIES_TBE_ERRORS: '/api/tax-entries/tbe-errors/:periodName',
    TAX_ENTRIES: '/api/tax-entries/:companyCode/:period/:taxYear/:countryTaxType/:bookType',
    TAX_ENTRY: '/api/tax-entries/:taxEntryId',
    CREATE_TAX_ENTRY: '/api/tax-entries',
    FETCH_TAX_ENTRIES_TREE: '/api/tax-entries-tree/:forEntry/:forReclass/:periodName/:companyCode/:taxYear/:countryTaxType',
    CREATE_TAX_ENTRIES: '/api/tax-entries/many',
    ACCOUNTS: '/api/accounts',
    BOOK_TYPES: '/api/book-types/:period',
    ADD_ADJUSTMENT: '/api/adjustments/:period',
    UPDATE_ADJUSTMENT: '/api/adjustments/:adjustmentId/:period',
    REMOVE_ADJUSTMENT: '/api/adjustments/:adjustmentId/:companyCode/:period',
    FETCH_ADJUSTMENT: '/api/adjustments/:adjustmentId/:period',
    ADD_ATTACHMENT: '/api/attachments',
    ADD_ATTACHMENTS: '/api/attachments/many',
    COMPANY_CODE_DETAILS: '/api/provisions/company-code-details/:companyCode/:period',
    FETCH_ADJUSTMENT_ATTACHMENTS: '/api/adjustments/:adjustmentId/:period/attachments',
    FETCH_SUBLEDGER_TAX_ENTRIES: '/api/tax-entries-tree/:period/:companyCode',
    FETCH_SUBLEDGER_GL_ACCOUNTS: '/api/gl-accounts/:period',
    FETCH_PROPOSED_JOURNALS: '/api/journals/:companyCode/:period?:queryString',
    FETCH_CALCULATION_ID: '/api/journals/archivalJournalCalculationId/:connName/:periodName',
    FETCH_JOURNAL_FILENAME: '/api/journals/fileName/:connName/:periodName/:calcId',
    FETCH_USER_PROFILE: '/api/users/profile',
    UPDATE_USER_PREFERENCES: '/api/users/preferences',
    UPDATE_USER_LAST_CLIENT: '/api/users/lastClient',
    FETCH_TAX_ENTRIES_FOR_APPROVAL: '/api/tax-entries/for-approval/:periodName/:preparerSSO',
    UNLOCK_TAX_ENTRIES_FOR_APPROVAL: '/api/tax-entries/lock/:lockId',
    APPROVE_TAX_ENTRIES: '/api/tax-entries/approve',
    DECLINE_TAX_ENTRIES: '/api/tax-entries/decline',
    FETCH_TAX_ENTRY_FOR_APPROVAL_ATTACHMENTS: '/api/tax-entries/:taxEntryId/attachments',
    FETCH_CONNECTIONS: '/api/connections/:period',
    FETCH_CONNECTIONS_BY_TYPE: '/api/connections/byConnType/:period/:connType',
    FETCH_ASSIGNED_CONNECTIONS: '/api/connections/assigned/x/:period',
    FETCH_NOTIFICATIONS: '/api/notifications/:period/:connection',
    FETCH_BSM_DIAGNOSTICS: '/api/bsm-diagnostics/:period/:connection',
    FETCH_ACCOUNT_ACTIVITY: '/api/account-activity/:periodName/:companyCode/:taxYear/:countryTaxType/:slAccount/:accountId/:bookType',

    FETCH_TAX_EXPENSE_REPORT: '/api/reports/taxExpense/:groupBy/:period/:bookType/:reportingCurrency/:countryTaxType/:groupItem?generateAsyncDetailsLink=:generateAsyncDetailsLink',
    FETCH_HISTORICAL_TAX_EXPENSE_REPORT: '/api/reports/taxExpenseHist/:groupBy/:period/:bookType/:reportingCurrency/:companyCodeToFilter',
    FETCH_DRILL_DETAIL: '/api/reports/drillDetail/:periodName/:reportType/:reportLineType/:reportingCurrency/:groupType/:groupItem/:funcCurrency/:taxCurrency/:detailAggregationType/:countryTaxType',
    FETCH_HISTORICAL_DRILL_DETAIL: '/api/reports/drillDetailHist/:periodName/:reportType/:reportLineType/:reportingCurrency/:groupType/:groupItem/:funcCurrency/:taxCurrency',
    FETCH_DRILL_DETAIL_FOR_EXPORT: '/api/reports/drillDetailExport/:periodName/:reportingCurrency/:groupType/:groupItem/:detailAggregationType/:countryTaxType',
    FETCH_HISTORICAL_DRILL_DETAIL_FOR_EXPORT: '/api/reports/drillDetailExportHist/:periodName/:reportingCurrency/:groupType/:companyCodeToFilter',

    FETCH_COUNTRY_TAX_TYPES_FOR_FOR_PERIOD: '/api/reports/taxExpense/countryTaxTypes/:periodName',

    FETCH_DIAGNOSTICS_TAX_BOOKING: '/api/diagnostics/tax-booking/:periodName',
    FETCH_DIAGNOSTICS_CURRENCIES: '/api/diagnostics/currencies/:periodName',
    FETCH_DIAGNOSTICS_OBLIGATION: '/api/diagnostics/obligation/:periodName',
    FETCH_DIAGNOSTICS_COMPANY_CODE: '/api/diagnostics/company-code/:periodName',
    FETCH_DIAGNOSTICS_LEGAL_ENTITY: '/api/diagnostics/legal-entity/:periodName',
    FETCH_DIAGNOSTICS_PROVISION: '/api/diagnostics/provision/:periodName',

    FETCH_BALANCE_SHEET_MANAGEMENT_REPORT: '/api/reports/:reportName?:queryString',
    LOGOUT: '/api/logout',
    BATCH_PROVISIONS_SET_STATUS_PROCESSING: '/api/provisions/batch-provisions/set-status/processing',
    BATCH_PROVISIONS: '/api/provisions/batch-provisions',
    BATCH_PROVISIONS_CONNECTIONS: '/api/provisions/batch-provisions/connections/:periodName',
    BATCH_PROVISIONS_STATUS: '/api/connections/:periodName/:connectionName',
    PROCESS_BATCH_PROVISIONS: '/api/provisions/process-batch-provisions',
    PROCESS_BATCH_PROVISIONS_SYNC: '/api/provisions/process-batch-provisions-sync',
    PROCESS_BATCH_PROVISIONS_FOR_GL_UI_CONNECTION: '/api/provisions/process-batch-provisions-gl-ui-connection',
    PROCESS_BATCH_PROVISIONS_FOR_FTP_CONNECTION: '/api/commands/proformaBatchCalc/:connName/:periodName',
    BATCH_TAX_ENTRIES: '/api/tax-entries/batch-tax-entries',
    BATCH_TAX_ENTRIES_VALIDATION_TREE: '/api/tax-entries-tree',
    VALIDATE_AND_PROCESS_TAX_ENTRIES: '/api/tax-entries/validate/:periodName',
    VALIDATE_BATCH_ADJUSTMENTS: '/api/batch-adjustments/validate',
    IMPORT_BATCH_ADJUSTMENTS: '/api/batch-adjustments/import',

    MAIN_PAGE_BUTTONS: '/api/page-help/',
    HELP_CONTENTS_FOR_EXPORT: '/api/page-help/content/all',
    INSERT_HELP_CONTENT: '/api/page-help/content',
    HELP_CONTENT: '/api/page-help/content/:contentId',
    SEARCH_DOCUMENTATION: '/api/page-help/search/:searchedText',
    RELEASE_NOTES: '/api/page-help/release-notes',

    FETCH_BUSINESS_UNITS: '/api/business-unit/:periodName',
    FETCH_BUSINESS_UNIT: '/api/business-unit/:businessUnit/:periodName',
    ADD_BUSINESS_UNIT: '/api/business-unit/:periodName',
    UPDATE_BUSINESS_UNIT: '/api/business-unit/:businessUnit/:periodName',
    DELETE_BUSINESS_UNIT: '/api/business-unit/:businessUnit/:periodName',
    FETCH_BUSINESS_CODES: '/api/business-codes',
    FETCH_LINE_OF_BUSINESS_VALUES: '/api/name-value-group/line_of_business',
    FETCH_ENABLED_FLAG_VALUES: '/api/name-value-group/yes_no',

    FETCH_ADJUSTMENT_PERIODS: '/api/beginning-balances/adjustment-periods',
    FETCH_COMPANY_CODES_BEGINNING_BALANCES: '/api/beginning-balances/company-codes/:adjustmentPeriod',
    FETCH_CONNECTIONS_BEGINNING_BALANCES: '/api/beginning-balances/connections/:adjustmentPeriod',
    FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES: '/api/beginning-balances/company-codes/connection/:connectionName',
    FETCH_BEGINNING_BALANCES_FOR_COMPANY_CODE: '/api/beginning-balances/:adjustmentPeriod/:companyCode',
    FETCH_BEGINNING_BALANCES_IMPORT_SUMMARY: '/api/beginning-balances/import-status/:adjustmentPeriod/:connectionName',
    FETCH_BEGINNING_BALANCES_VALIDATION_SUMMARY: '/api/beginning-balances/validation-summary/:adjustmentPeriod/:companyCode',

    FETCH_CONNECTION_RUN_STATUS: '/api/connections/stats/:periodName/:connectionName',

    FETCH_TOTAL_QUALITY: '/api/provision-quality/total/:periodName',
    FETCH_CONNECTION_QUALITY: '/api/provision-quality/connections/:periodName/:connectionName',
    FETCH_CONNECTIONS_QUALITY: '/api/provision-quality/connections/:periodName',
    FETCH_BUSINESS_QUALITY: '/api/provision-quality/businesses/:periodName/:businessCode',
    FETCH_BUSINESSES_QUALITY: '/api/provision-quality/businesses/:periodName',
    FETCH_USEFUL_STATS: '/api/provision-quality/stats/:periodName',
    FETCH_CONNECTION_MONITOR_DATA: '/api/connections/stats/panel/:periodName',
    FETCH_NOTIFICATIONS_PANEL_DATA: '/api/notifications/panel/:periodName',

    FETCH_HEARTBEAT_MONITOR_STATUS: '/api/heartbeatcheck',

    FETCH_TAX_ACCOUNTINGS: '/api/tax-accounting/:periodName',
    FETCH_TAX_ACCOUNTING: '/api/tax-accounting/:taxDescription/:periodName',
    ADD_TAX_ACCOUNTING: '/api/tax-accounting/:periodName',
    UPDATE_TAX_ACCOUNTING: '/api/tax-accounting/:taxDescription/:periodName',
    DELETE_TAX_ACCOUNTING: '/api/tax-accounting/:taxDescription/:periodName',
    VALIDATE_TAX_ACCOUNTING: '/api/tax-accounting/validate',
    SAVE_TAX_ACCOUNTING: '/api/tax-accounting/import',
    FETCH_TAX_DESCRIPTION_NAMES: '/api/tax-accounting/taxDescriptionNames',

    FETCH_COMMUNICATIONS: '/api/communications',
    FETCH_ADMIN_COMMUNICATIONS: '/api/communications/admin',
    ADD_COMMUNICATION: '/api/communications/add',
    REMOVE_COMMUNICATION: '/api/communications/:communicationId',
    EDIT_COMMUNICATION: '/api/communications/edit',

    FETCH_TAX_SETTLEMENT_ACCOUNTS: '/api/tax-settlement-accounts/:periodName',
    FETCH_TAX_SETTLEMENT_ACCOUNT: '/api/tax-settlement-accounts/:periodName/:sourceAccount/:lineOfBusiness',
    ADD_TAX_SETTLEMENT_ACCOUNT: '/api/tax-settlement-accounts/:periodName',
    UPDATE_TAX_SETTLEMENT_ACCOUNT: '/api/tax-settlement-accounts/:periodName',
    DELETE_TAX_SETTLEMENT_ACCOUNT: '/api/tax-settlement-accounts/:periodName/:sourceAccount/:lineOfBusiness',

    FETCH_NON_US_COMPANY_CODES: '/api/company-codes/non-us/:periodName',
    FETCH_ENABLED_COMPANY_CODES: '/api/company-codes/enabled/:periodName',
    FETCH_COUNTRY_TAX_TYPES: '/api/company-codes/country-tax-types-non-us/:companyCode/:periodName',
    FETCH_COMPANY_CODES_BY_GROUP_ITEM: '/api/company-codes/:groupType/:groupItem',

    FETCH_TAX_RATE_CHANGES: '/api/tax-rate-changes/:periodName',

    FETCH_USERS: '/api/users',
    FETCH_USERS_EXCEL_DATA: '/api/users/excelData',
    FETCH_USERS_EXPIRATION: '/api/users/expiration/:clientId',
    FETCH_USER_SETTINGS_FORM_DATA: '/api/users/settings-data/:periodName',
    FETCH_USER_SETTINGS: '/api/users/:sso?excludeRoleAssignments=:excludeRoleAssignments',
    ADD_USER_SETTINGS: '/api/users',
    UPDATE_USER_SETTINGS: '/api/users/:sso',
    SET_USER_LAST_ID: '/api/users/admin-set-last-client',
    VALIDATE_USERS: '/api/users/validate',
    IMPORT_USERS: '/api/users/import',
    UPDATE_USERS_EXPIRATION: '/api/users/expiration',

    CHECK_IF_USER_SSO_EXISTS: '/api/users/check-user-sso',
    CHECK_IF_USER_EMAIL_EXISTS: '/api/users/check-user-email',
    CHECK_IF_USER_PWC_EMAIL_SETUP: '/api/users/check-user-pwc-email-setup',
    CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN: '/api/users/check-user-email-under-domain',

    FETCH_TLS_ACCOUNTS: '/api/tls-accounts/:periodName',
    FETCH_TLS_ACCOUNT: '/api/tls-accounts/:account/:periodName',
    ADD_TLS_ACCOUNT: '/api/tls-accounts/:periodName',
    IMPORT_TLS_ACCOUNT: '/api/tls-accounts/import',
    UPDATE_TLS_ACCOUNT: '/api/tls-accounts/:account/:periodName',
    DELETE_TLS_ACCOUNT: '/api/tls-accounts/:account/:periodName',
    VALIDATE_TLS_ACCOUNT: '/api/tls-accounts/validate',
    GENERATE_R2R_CHART_TLS_ACCOUNTS: '/api/tls-accounts/generateR2R/:periodName',


    FETCH_SL_ACCOUNTS_FOR_ACCOUNT_BALANCES: '/api/account-balances/sl-accounts/:periodName',
    FETCH_ACCOUNT_BALANCES: '/api/account-balances/:periodName/:displayBy',

    FETCH_NOL_AND_VA_FLAGS: '/api/nol-and-va-flag/:periodName',
    UPDATE_NOL_AND_VA_FLAGS: '/api/nol-and-va-flag',
    FETCH_FLAG_NAMES_AND_VALUES: '/api/nol-and-va-flag/flags/:groupName',

    FETCH_LEGAL_ENTITIES: '/api/legal-entities/:periodName',
    FETCH_LEGAL_ENTITY: '/api/legal-entities/:legalEntity/:periodName',
    ADD_LEGAL_ENTITY: '/api/legal-entities/:periodName',
    UPDATE_LEGAL_ENTITY: '/api/legal-entities/:legalEntity/:periodName',
    VALIDATE_LEGAL_ENTITY: '/api/legal-entities/validate',
    SAVE_LEGAL_ENTITIES: '/api/legal-entities/import',
    FETCH_US_TAX_TYPES: '/api/name-value-group/us_tax_type',
    FETCH_LOCAL_TAX_TYPES: '/api/name-value-group/local_tax_type',
    FETCH_COUNTRIES: '/api/countries',
    CREATE_COUNTRY: '/api/countries',
    UPDATE_COUNTRY: '/api/countries',
    DELETE_COUNTRY: '/api/countries',

    FETCH_TAX_BOOKING_ENTITIES: '/api/tax-booking-entities/:periodName',
    FETCH_TAX_BOOKING_ENTITY: '/api/tax-booking-entities/:periodName/:companyCode/:legalEntity/:tbeLegalEntity/:tbeType',
    FETCH_TAX_BOOKING_ENTITY_TYPES: '/api/name-value-group/tbe_type',
    FETCH_ENABLED_TBE_COMPANY_CODES: '/api/tax-booking-entities/tbe-enabled/:periodName/:companyCode/:tbeLegalEntity',
    ADD_TAX_BOOKING_ENTITY: '/api/tax-booking-entities/:periodName',
    UPDATE_TAX_BOOKING_ENTITY: '/api/tax-booking-entities/:periodName/:companyCode/:legalEntity/:tbeLegalEntity/:tbeType',
    DELETE_TAX_BOOKING_ENTITY: '/api/tax-booking-entities/:periodName/:companyCode/:legalEntity/:tbeLegalEntity/:tbeType',
    FETCH_IMPORT_STATUS_VALUES: '/api/status/import',
    REFRESH_TAX_BOOKING_ENTITIES: '/api/tax-booking-entities/refresh',


    FETCH_CURRENCY_CONVERSION_RATES: '/api/currency-conversion-rates/:periodName',
    INSERT_CURRENCY_CONVERSION_RATE: '/api/currency-conversion-rates',
    UPDATE_CURRENCY_CONVERSION_RATE: '/api/currency-conversion-rates',
    DELETE_CURRENCY_CONVERSION_RATE: '/api/currency-conversion-rates/:periodName/:fromCurrency/:toCurrency',
    VALIDATE_CURRENCY_CONVERSION_RATES: '/api/currency-conversion-rates/validate',
    IMPORT_CURRENCY_CONVERSION_RATES: '/api/currency-conversion-rates/import',

    FETCH_GL_ACCOUNT_LIST: '/api/gl-account-list/:periodName',
    ADMIN_IMPORT_GL_ACCOUNT_LIST: '/api/gl-account-list/admin-import',
    VALIDATE_GL_ACCOUNT_LIST: '/api/gl-account-list/validate',
    IMPORT_GL_ACCOUNT_LIST: '/api/gl-account-list/import',
    DELETE_GL_ACCOUNT_LIST: '/api/gl-account-list/:periodName/:listName/:glAccount',
    INSERT_GL_ACCOUNT_LIST: '/api/gl-account-list',
    UPDATE_GL_ACCOUNT_LIST: '/api/gl-account-list/:periodName/:listName/:glAccount',

    FETCH_ACCOUNT_MAP: '/api/account-map/findAllByPerdiod/:periodName',
    DELETE_ACCOUNT_MAP: '/api/account-map/delete/:periodName/:bsmAccount/:glAccount',
    INSERT_ACCOUNT_MAP: '/api/account-map/insert',
    UPDATE_ACCOUNT_MAP: '/api/account-map/update/:periodName/:bsmAccount/:glAccount',
    ADMIN_IMPORT_ACCOUNT_MAP: '/api/account-map/admin-import',
    VALIDATE_ACCOUNT_MAP: '/api/account-map/validate',
    IMPORT_ACCOUNT_MAP: '/api/account-map/import',

    FETCH_TAX_RATES: '/api/tax-rates/:periodName',
    IMPORT_TAX_RATES: '/api/tax-rates/import',
    FETCH_TAX_RATE: '/api/tax-rates/:periodName/:countryTaxType/:countryCode',
    ADD_TAX_RATE: '/api/tax-rates/:periodName',
    UPDATE_TAX_RATE: '/api/tax-rates/:periodName',
    DELETE_TAX_RATE: '/api/tax-rates/:periodName/:countryTaxType/:countryCode',
    VALIDATE_TAX_RATES: '/api/tax-rates/validate/:periodName',

    FETCH_SL_ADMIN_ACCOUNTS: '/api/sl-admin-account/:periodName',
    IMPORT_SL_ADMIN_ACCOUNTS: '/api/sl-admin-account/import',
    ADMIN_IMPORT_SL_ADMIN_ACCOUNTS: '/api/sl-admin-account/admin/import',
    FETCH_SL_ACCOUNTS: '/api/sl-admin-account/sl-accounts/:periodName',
    FETCH_SL_VIEWS: '/api/sl-admin-account/sl-views',
    UPDATE_SL_ADMIN_ACCOUNT: '/api/sl-admin-account/:periodName/:slAccount',
    INSERT_SL_ADMIN_ACCOUNT: '/api/sl-admin-account',
    DELETE_SL_ADMIN_ACCOUNT: '/api/sl-admin-account/:periodName/:slAccount',
    VALIDATE_SL_ADMIN_ACCOUNT: '/api/sl-admin-account/validate',

    ADD_COMPANY_CODE: '/api/company-codes',
    ADMIN_IMPORT_COMPANY_CODES: '/api/company-codes/admin/import',
    UPDATE_COMPANY_CODE: '/api/company-codes',
    FETCH_COMPANY_CODES_ADMIN: '/api/company-codes/admin/:periodName',
    FETCH_COMPANY_CODE_ADMIN: '/api/company-codes/admin/:periodName/:companyCode',
    FETCH_COMPANY_CODE_ADMIN_FORM_CONTROL_DATA: '/api/company-codes/admin/form-data/lookup/:periodName',
    FETCH_US_SHAREHOLDER_FLAG_VALUES: '/api/name-value-group/us_shareholder_flag',
    FETCH_CHART_EDIT_VALIDATION: '/api/charts/validate/:chartName/:periodName',
    FETCH_CHART_NAMES_ALL: '/api/charts/names-all/:periodName',
    FETCH_CHART_NAMES_BY_USER: '/api/charts/names-user/:periodName',
    VALIDATE_COMPANY_CODES: '/api/company-codes/validate',
    IMPORT_COMPANY_CODES: '/api/company-codes/import',
    FETCH_BUSINESS_DESCRIPTIONS_FOR_IMPORT: '/api/company-codes/businessCodes',

    FETCH_USER_ASSIGNED_COMPANY_CODES: '/api/company-codes/user-assigned/:periodName',

    FETCH_CONNECTION_NAMES_ADMIN: '/api/connections/conn/names/:periodName',
    FETCH_CURRENCY_CODES_ENABLED: '/api/currencies/codes',
    FETCH_REPORTING_CURRENCY_CODES: '/api/currencies/reporting-currency-codes',

    FETCH_ADMIN_CURRENCIES: '/api/currencies/admin',
    CREATE_CURRENCY: '/api/currencies/admin',
    UPDATE_CURRENCY: '/api/currencies/admin',
    DELETE_CURRENCY: '/api/currencies/admin',

    FETCH_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/:periodName',
    DELETE_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/:periodName/:exceptionId',
    BATCH_UPDATE_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/batch-update/:periodName',
    IMPORT_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/import/:periodName',
    VALIDATE_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/validate/:periodName',
    ADD_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/:periodName',
    REFRESH_ADMIN_BOOK_TO_TAX_DIFFERENCES: '/api/book-to-tax-differences/refresh/:periodName',

    FETCH_GL_JOURNALS: '/api/gl-journal/:periodName',
    FETCH_GL_ACCOUNTS: '/api/gl-journal/gl-accounts/:periodName',
    IMPORT_GL_ACCOUNTS: '/api/gl-journal/import',
    FETCH_TAXEXTRY_TYPES: '/api/name-value-group/taxentry_type',
    FETCH_TAXEXTRY_ACTIVITIES: '/api/name-value-group/taxentry_activity',
    FETCH_SL_TAX_ENTRIES: '/api/gl-journal/sl-tax-entries/:periodName',

    INSERT_GL_JOURNAL: '/api/gl-journal',
    DELETE_GL_JOURNAL: '/api/gl-journal/:periodName/:countryTaxType/:type/:activity/:slAccount',
    UPDATE_GL_JOURNAL: '/api/gl-journal/:periodName/:countryTaxType/:type/:activity/:slAccount',
    VALIDATE_GL_JOURNAL: '/api/gl-journal/validate',
    IMPORT_GL_JOURNAL: '/api/gl-journal/import',

    FETCH_ADMIN_CONNECTIONS: '/api/admin-connections/:periodName',
    FETCH_ADMIN_CONNECTION: '/api/admin-connections/:connName/:periodName',
    ADD_ADMIN_CONNECTION: '/api/admin-connections',
    UPDATE_ADMIN_CONNECTION: '/api/admin-connections',
    FETCH_CONTENT_MANAGEMENT_QUERIES: '/api/queries',
    ADD_CONTENT_MANAGEMENT_QUERY: '/api/queries',
    UPDATE_CONTENT_MANAGEMENT_QUERY: '/api/queries/:queryId',
    DELETE_CONTENT_MANAGEMENT_QUERY: '/api/queries/:queryId',
    TEST_CONTENT_MANAGEMENT_QUERY: '/api/queries/test',
    FETCH_YES_NO_FLAG: '/api/name-value-group/yes_no',
    FETCH_CONNECTION_TYPE: '/api/name-value-group/connection_type',
    FETCH_INBOUND_FORMAT: '/api/name-value-group/inbound_format',
    FETCH_OUTBOUND_FORMAT: '/api/name-value-group/outbound_format',
    FETCH_BYPASS_TB_VALIDATION_TYPE: '/api/name-value-group/bypass_validation',
    FETCH_ADMIN_CHART_ACCOUNTS: '/api/charts/master/:chartName/:periodName',
    ADD_ADMIN_CHART: '/api/charts/master/:chartName/:periodName',
    UPDATE_ADMIN_CHART: '/api/charts/master/:chartName/:originalChartName/:periodName',
    DELETE_ADMIN_CHART: '/api/charts/:chartName/:periodName',
    ADD_ADMIN_CHART_ACCOUNT: '/api/charts/account/:chartName/:periodName',
    UPDATE_ADMIN_CHART_ACCOUNT: '/api/charts/account/:chartName/:localAccount/:periodName',
    DELETE_ADMIN_CHART_ACCOUNT: '/api/charts/account/:chartName/:localAccount/:periodName',
    IMPORT_ADMIN_CHART: '/api/charts/import/:chartName/:periodName',

    FETCH_CATEGORIES_LIST: '/api/categories/:periodName',
    FETCH_CATEGORY_VIEWS_LIST: '/api/categories/category-views',
    FETCH_CATEGORIES_WITH_TAGS_LIST: '/api/categories/with-tags/:periodName',
    FETCH_TAGS_FOR_CATEGORY_LIST: '/api/categories/:periodName/:categoryName',
    EDIT_CATEGORY: '/api/categories/:periodName/:categoryName',
    DELETE_CATEGORY: '/api/categories/:periodName/:categoryName',
    ADD_CATEGORY: '/api/categories/:periodName',
    FETCH_TAGS: '/api/tags/:periodName',
    ADD_TAG: '/api/tags/:periodName/:categoryName',
    UPDATE_TAG: '/api/tags/:periodName/:categoryName/:tagName',
    DELETE_TAG: '/api/tags/:periodName/:categoryName/:tagName',
    CATEGORY_DETAILS: '/api/categories/category/:periodName/:categoryName',
    VALIDATE_TAGS: '/api/tags/:periodName/:categoryName/validate',
    IMPORT_TAGS: '/api/tags/:periodName/:categoryName/import',

    FETCH_CLIENT_LIST: '/api/client',
    DELETE_CLIENT: '/api/client/:clientId',
    INSERT_CLIENT: '/api/client',
    UPDATE_CLIENT: '/api/client/:clientId',
    FETCH_USER_CLIENT_LIST: '/api/client/forUser',
    UPDATE_MAINTENANCE_MODE_FOR_MULTIPLE_CLIENTS: '/api/client/maintenance-flag',

    FETCH_ADMIN_TAX_ENTRIES: '/api/admin-tax-entries/:periodName',
    FETCH_TYPE_DESCRIPTIONS: '/api/admin-tax-entries/types/:periodName',
    FETCH_ACTIVITY_DESCRIPTIONS: '/api/admin-tax-entries/activities/:periodName',
    IMPORT_ADMIN_TAX_ENTRIES: '/api/admin-tax-entries/import',
    INSERT_ADMIN_TAX_ENTRY: '/api/admin-tax-entries/:periodName',
    UPDATE_ADMIN_TAX_ENTRY: '/api/admin-tax-entries/:type/:activity/:periodName',
    DELETE_ADMIN_TAX_ENTRY: '/api/admin-tax-entries/:type/:activity/:periodName',
    VALIDATE_ADMIN_TAX_ENTRIES: '/api/admin-tax-entries/validate',
    FETCH_TAX_ENTRY_TYPE: '/api/name-value-group/taxentry_type',
    FETCH_TAX_ENTRY_ACTIVITY: '/api/name-value-group/taxentry_activity',
    FETCH_TAX_ENTRY_VIEW: '/api/name-value-group/taxentry_view',

    FETCH_CONFIGURATION_SETTINGS_COMPANY_CODES: '/api/configuration-settings/company-codes/:periodName',
    FETCH_CONFIGURATION_SETTINGS_BUSINESS_UNITS: '/api/configuration-settings/business-units/:periodName',
    FETCH_CONFIGURATION_SETTINGS_LEGAL_ENTITIES: '/api/configuration-settings/legal-entities/:periodName',
    FETCH_CONFIGURATION_SETTINGS_TAX_RATES: '/api/configuration-settings/tax-rates/:periodName',
    FETCH_CONFIGURATION_SETTINGS_BOOK_TO_TAX_DIFFERENCES: '/api/configuration-settings/book-to-tax-differences/:periodName',
    FETCH_CONFIGURATION_SETTINGS_OBLIGATIONS: '/api/configuration-settings/obligations/:periodName',
    FETCH_CONFIGURATION_SETTINGS_TAX_BOOKING_ENTITIES: '/api/configuration-settings/tax-booking-entities/:periodName',
    FETCH_CONFIGURATION_SETTINGS_TLS_ACCOUNTS: '/api/configuration-settings/tls-accounts/:periodName',
    FETCH_CONFIGURATION_SETTINGS_TAX_ACCOUNTING: '/api/configuration-settings/tax-accounting/:periodName',
    FETCH_CONFIGURATION_SETTINGS_USERS: '/api/configuration-settings/users',
    FETCH_CONFIGURATION_SETTINGS_GL_JOURNALS: '/api/configuration-settings/gl-journals/:periodName',
    FETCH_CONFIGURATION_SETTINGS_CURRENCIES: '/api/configuration-settings/currencies',
    FETCH_CONFIGURATION_SETTINGS_PERIODS: '/api/configuration-settings/periods',
    FETCH_CONFIGURATION_SETTINGS_CURRENCY_CONVERSION_RATES: '/api/configuration-settings/currency-conversion-rates/:periodName',

    FETCH_SL_GROUP_INITIAL_DATA: '/api/sl-groups/initial-data/:periodName?groupType=:groupType&groupItem=:groupItem&countryTaxType=:countryTaxType&includeFbosCTT=:includeFbosCTT',
    FETCH_SL_GROUP_TYPES: '/api/sl-groups/types',
    FETCH_SL_GROUP_ITEMS: '/api/sl-groups/group-items/:periodName/:groupType',
    FETCH_COUNTRY_TAX_TYPES_FOR_GROUP: '/api/sl-groups/country-tax-types/:groupType/:groupItem/:periodName',
    FETCH_COUNTRY_TAX_TYPES_FOR_GROUP_AND_RATES: '/api/sl-groups/country-tax-types-and-rates/:groupType/:groupItem/:periodName',
    FETCH_REPORT_DATA_FOR_GROUP: '/api/sl-groups/report-data-group/:groupType/:groupItem/:countryTaxType',
    FETCH_REPORTING_CURRENCY_CODES_FOR_GROUP: '/api/sl-groups/currencies-for-group/:groupType/:groupItem',

    FETCH_CUSTOM_REPORTS: '/api/custom-reports',
    FETCH_ENABLED_CUSTOM_REPORTS: '/api/custom-reports/enabled',
    FETCH_CUSTOM_REPORT_SET: '/api/custom-reports/:reportSetId/reports',
    SAVE_CUSTOM_REPORT: '/api/custom-reports',
    UPDATE_CUSTOM_REPORT: '/api/custom-reports/:reportSetId',
    UPDATE_CUSTOM_REPORT_DISPLAY_FLAG: '/api/custom-reports/:reportSetId/updateDisplayFlag',
    DELETE_CUSTOM_REPORT: '/api/custom-reports/:reportSetId',
    FETCH_CUSTOM_REPORT_INSERT_QUERY: '/api/custom-reports/:reportSetId/insert-query',

    FETCH_CUSTOM_ETR_DRIVERS: '/api/etr-manual-entry/:periodName/:groupType/:groupItem',
    ADD_CUSTOM_ETR_DRIVER: '/api/etr-manual-entry',
    UPDATE_CUSTOM_ETR_DRIVER: '/api/etr-manual-entry',
    DELETE_CUSTOM_ETR_DRIVER: '/api/etr-manual-entry/:periodName/:manualEntryId',
    FETCH_COMPANY_CODES_FOR_ETR: '/api/etr-manual-entry/getCompanyCodes',
    FETCH_COUNTRY_TAX_TYPES_FOR_ETR: '/api/etr-manual-entry/countryTaxTypes/:periodName/:groupType/:groupItem',
    FETCH_COUNTRY_TAX_TYPES_FOR_FOR_COMPANY_CODE: '/api/etr-manual-entry/countryTaxTypes/:periodName/:companyCode',
    FETCH_ETR_REPORT_DATA: '/api/etr-report/get-report',
    FETCH_TAGS_FOR_ETR: '/api/etr-report/tags/:periodName',
    FETCH_GROUP_ITEMS_FOR_ETR: '/api/etr-report/group-items/:groupType',

    FETCH_CUSTOM_GROUPS: '/api/custom-groups',
    FETCH_CUSTOM_GROUPS_WITH_COMPANY_CODES: '/api/custom-groups/with-company-codes/:periodName',
    FETCH_CUSTOM_GROUP_ITEMS: '/api/custom-groups/group-items/:groupType',
    DELETE_CUSTOM_GROUP: '/api/custom-groups/:groupId',
    EDIT_CUSTOM_GROUP: '/api/custom-groups/edit',
    SAVE_NEW_CUSTOM_GROUP: '/api/custom-groups/create',
    REFRESH_CUSTOM_GROUP_DATA: '/api/custom-groups/refresh',

    FETCH_BATCH_REPORT: '/api/batch-reports/:batchReportId',
    FETCH_BATCH_REPORT_ARCHIVE_COMPANY_CODES: '/api/batch-reports/archive-company-codes/:periodName/:entity',
    FETCH_CUSTOM_GROUP_ITEM_TYPES: '/api/custom-groups/item-types/:groupType',
    DOWNLOAD_BATCH_REPORT: '/api/batch-reports/:jobId/download',
    DOWNLOAD_BATCH_ZIP_REPORT: '/api/batch-reports/:fileName/download/zip',
    DOWNLOAD_JOURNAL_FILE: '/api/admin-connections/:jobId/download',

    FETCH_BATCH_REPORTS_OPTIONS_FOR_REPORT: '/api/batch-reports/options-for-report/:reportName',

    TEST_CUSTOM_REPORT: '/api/custom-reports/test',
    RUN_CUSTOM_REPORT: '/api/custom-reports/run',
    RUN_VALIDATE_REPORT: '/api/custom-reports/validate',
    EXPORT_CUSTOM_REPORT: '/api/custom-reports/export',
    EXPORT_BATCH_REPORT: '/api/batch-reports/export',
    GET_EXPORT_JOB_STATUS: '/api/trg/job/:jobId',
    DOWNLOAD_CUSTOM_REPORT: '/api/trg/file/:jobId',
    FETCH_CUSTOM_REPORT_SET_PARAMS: '/api/custom-reports/:reportSetId/params',
    FETCH_CUSTOM_REPORT_PARAMS: '/api/custom-reports/get-company-codes/:periodName',
    SAVE_ARCHIVE_REPORT: '/api/custom-reports/:batchId/updateDefaultParams',

    MULTI_PERIOD_ANALYSIS: '/api/multi-period-analysis',

    FETCH_APPLICATION_SETTINGS_FLAGS: '/api/application-settings',
    UPDATE_APPLICATION_SETTINGS_FLAGS: '/api/application-settings',
    CLEAR_DEV_CACHE: '/api/cache/clear',
    MATERIALIZED_VIEW_REFRESH: '/api/application-settings/materializedViewRefresh',
    UNLOCK_ALL_TAX_ENTRIES: '/api/tax-entries/lock/all',
    FETCH_ADMIN_JOBS: '/api/application-settings/admin-jobs',
    RUN_ADMIN_JOB: '/api/application-settings/admin-jobs/:jobName',
    RUN_DEACTIVATE_USERS_JOB: '/api/application-settings/run-deactivate-users-job',
    CLEAR_BSM_BALANCES: '/api/application-settings/clear-bsm-balances/:companyCode/:periodName',
    TEST_REDIS_CONNECTION: '/api/application-settings/test-redis-connection',
    CLEAR_BSM_BALANCES_IN_ALL_PERIODS: '/api/application-settings/clear-bsm-balances-in-all-periods',
    ROLL_TO_NEXT_PERIOD: '/api/application-settings/roll-to-next-period',
    REFRESH_LE_CC: '/api/application-settings/refresh-le-cc',
    FETCH_MAINTENANCE_AND_LOCK_FLAG: '/api/application-settings/maintenance-and-lock-flag',
    UPDATE_MAINTENANCE_FLAG: '/api/application-settings/maintenance-flag',
    UPDATE_CLIENT_LOCK_FLAG: '/api/application-settings/lock-flag',
    FETCH_E2E_NOTIFICATIONS: '/api/application-settings/e2e-notifications',
    UPDATE_E2E_NOTIFICATION_MASTER: '/api/application-settings/e2e-notifications/master/:jobId',
    UPDATE_E2E_NOTIFICATION_CLIENT: '/api/application-settings/e2e-notifications/client/:jobId',

    ADD_BUSINESS_CODE: '/api/business-codes',
    SUPPORT_REQUEST: '/api/support-request',
    SUPPORT_REQUEST_PUBLIC: '/api/public/support-request',

    XLSX_EXPORT: '/api/xlsx/export/:filename',

    FIND_DEFERRED_TAX_RATE: '/api/obligations/def-tax-rate/:periodName/:companyCode/:countryTaxType',
    FIND_OBLIGATION_RATES: '/api/obligations/rates/:periodName/:companyCode/:countryTaxType',
    REFRESH_OBLIGATIONS: '/api/obligations/refresh',
    FETCH_OBLIGATIONS: '/api/obligations/:periodName',
    UPDATE_OBLIGATION: '/api/obligations/:periodName',
    CREATE_OBLIGATION: '/api/obligations/:periodName',
    FETCH_COUNTRY_TAX_TYPES_FOR_OBLIGATIONS: '/api/obligations/:periodName/countryTaxTypes',
    FETCH_TAX_CURRENCY_DEFAULT_CODES_FOR_OBLIGATIONS: '/api/obligations/tax-currency-default-codes',
    VALIDATE_OBLIGATIONS: '/api/obligations/:periodName/validate',
    IMPORT_OBLIGATIONS: '/api/obligations/:periodName/import',
    REMOVE_OBLIGATION: '/api/obligations/:periodName/:companyCode/:countryCode/:countryTaxType',
    FETCH_FILING_GROUPS_FOR_OBLIGATIONS: '/api/obligations/filing-groups/:periodName',
    OBLIGATIONS_CHECK_TAX_RATES_TO_UPDATE: '/api/obligations/tax-rates-to-update',

    FETCH_BOOK_TO_TAX_CODE_DESCRIPTIONS: '/api/book-to-tax-code-descriptions/:periodName',
    ADD_BOOK_TO_TAX_CODE_DESCRIPTION: '/api/book-to-tax-code-descriptions',
    UPDATE_BOOK_TO_TAX_CODE_DESCRIPTION: '/api/book-to-tax-code-descriptions',
    REMOVE_BOOK_TO_TAX_CODE_DESCRIPTION: '/api/book-to-tax-code-descriptions/:periodName/:tagCode/:countryTaxType',
    VALIDATE_BOOK_TO_TAX_CODE_DESCRIPTIONS: '/api/book-to-tax-code-descriptions/validate',
    IMPORT_BOOK_TO_TAX_CODE_DESCRIPTIONS: '/api/book-to-tax-code-descriptions/import',

    IS_SUBDIVISION: '/api/country-tax-types/subdivision/:countryTaxType',
    FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS: '/api/country-tax-types/:periodName',

    FETCH_TRIAL_BALANCE_THRESHOLD: '/api/trial-balance-threshold/:periodName/:connectionName',
    UPDATE_TRIAL_BALANCE_THRESHOLD: '/api/trial-balance-threshold/:periodName/:connectionName',
    VALIDATE_TRIAL_BALANCE_THRESHOLD: '/api/trial-balance-threshold/:periodName/:connectionName/validate',
    IMPORT_TRIAL_BALANCE_THRESHOLD: '/api/trial-balance-threshold/:periodName/:connectionName/import',
    FETCH_HISTORICAL_TRIAL_BALANCE_DATA: '/api/trial-balances/generateHistoricalTrialBalanceFile/:calcId',
    FETCH_TRIAL_BALANCE_CALCULATION_ID: '/api/trial-balances/calculationId/:connName/:periodName',

    ADD_JOB: '/api/jim/:jobType',
    FETCH_JOB_STATUSES: '/api/jim/jobs',
    MARK_NOTIFICATIONS_AS_SEEN: '/api/jim/mark-notifications-as-seen',
    FETCH_JOB_RESULTS: '/api/jim/results/:jobId',
    DELETE_JOB_RESULTS: '/api/jim/results/:jobId',
    ADD_JOB_ON_MASTER: '/api/jim/insert-job-on-master/:jobType',

    DB_LOG_HEADERS: '/api/db-logs/headers/:from/:to/:note',
    DB_LOG_DETAIL: '/api/db-logs/detail/:headerId/:note',

    FETCH_SCENARIOS: '/api/scenarios',
    FETCH_SCENARIO_METADATA: '/api/scenarios/:scenarioId',
    CREATE_SCENARIO: '/api/scenarios',
    UPDATE_SCENARIO: '/api/scenarios/:scenarioId',
    REMOVE_SCENARIO: '/api/scenarios/:scenarioId',
    CHECK_IF_SCENARIO_NAME_EXISTS: '/api/scenarios/exists/byName/:scenarioName?excludeScenarioId=:excludeScenarioId',
    CALCULATE_SCENARIO: '/api/scenarios/:scenarioId/calc',
    FETCH_SCENARIO_ALLOWED_ASSIGNMENTS: '/api/scenarios/allowed-assignments/:periodName',

    FETCH_SCENARIO_COMPANY_CODES: '/api/scenarios/:scenarioId/company-codes/:scenarioEnabled',
    ADD_SCENARIO_MEMBER: '/api/scenarios/:scenarioId/company-codes',
    EDIT_SCENARIO_MEMBER: '/api/scenarios/:scenarioId/company-codes/:companyCode',
    ASSIGN_COMPANY_CODES_TO_SCENARIO: '/api/scenarios/:scenarioId/company-codes-associations',
    DISASSOCIATE_COMPANY_CODE_FROM_SCENARIO: '/api/scenarios/:scenarioId/company-codes-associations',
    DELETE_SCENARIO_MEMBER: '/api/scenarios/:scenarioId/company-codes/:companyCode',

    FETCH_SCENARIO_OBLIGATIONS: '/api/scenarios/:scenarioId/obligations',
    ADD_SCENARIO_OBLIGATION: '/api/scenarios/:scenarioId/obligations',
    UPDATE_SCENARIO_OBLIGATION: '/api/scenarios/:scenarioId/obligations/:companyCode/:countryCode/:countryTaxType',
    DELETE_SCENARIO_OBLIGATION: '/api/scenarios/:scenarioId/obligations/:companyCode/:countryCode/:countryTaxType',

    FETCH_SCENARIO_CALC_DATA: '/api/scenarios/:scenarioId/report/:reportName/search',
    FETCH_SCENARIO_REPORT_FIELDS: '/api/scenarios/report-fields',
    FETCH_SCENARIO_COMPARE_CALC_DATA: '/api/scenarios/:scenarioId/report/:reportName/compare/:compareScenarioId/search',
    FETCH_SCENARIO_DETAILS_FOR_REPORT_EXPORT: '/api/scenarios/:scenarioId/provision-report-details/search',

    FETCH_SCENARIO_MEMBERS_DATA: '/api/scenarios/:scenarioId/members/report/:reportName?companyCode=:companyCode&columns=:columns&reportingCurrency=:reportingCurrency',
    FETCH_SCENARIO_COMPARE_MEMBERS_DATA: '/api/scenarios/:scenarioId/members/report/:reportName/compare/:compareScenarioId?companyCode=:companyCode&columns=:columns&reportingCurrency=:reportingCurrency',

    FETCH_SCENARIO_DETAILS: '/api/scenarios/:scenarioId/report/:reportName/field/:fieldName/details?companyCode=:companyCode&countryTaxType=:countryTaxType&reportingCurrency=:reportingCurrency',
    BATCH_SCENARIO_DETAILS: '/api/scenarios/batch-details',
    CREATE_SCENARIO_DETAIL: '/api/scenarios/:scenarioId/report/:reportName/field/:fieldName/details',
    CREATE_SCENARIO_DETAIL_PAIR: '/api/scenarios/:baseScenarioId/:compareScenarioId/report/:reportName/field/:fieldName/details',
    EDIT_SCENARIO_DETAIL: '/api/scenarios/:scenarioId/report/:reportName/details/:detailId/:companyCode/:countryTaxType/:fieldName',
    REMOVE_SCENARIO_DETAIL: '/api/scenarios/:scenarioId/report/:reportName/details/:detailId/:companyCode/:countryTaxType/:fieldName',

    UPDATE_SCENARIO_REPORT_VALUE: '/api/scenarios/:scenarioId/report/:reportName',

    UPLOAD_SCENARIO_TAGGED_DATA: '/api/scenarios/:scenarioId/tagged-data',

    FETCH_ALLOWED_PERIODS_FOR_SCENARIOS: '/api/scenarios/allowed-periods',

    UPDATE_SESSION_TOKEN: '/api/session-token/update-session-token',

    GET_SESSION_STATUS: '/api/session',
    REFRESH_SESSION: '/api/test-auth',

    IDLE_PROCESSES: '/api/idle-processes',

    FETCH_ROLES: '/api/roles',
    FETCH_ROLE_ASSIGNMENTS: '/api/roles/:roleId',
    INSERT_ROLE: '/api/roles',
    UPDATE_ROLE: '/api/roles/:roleId',
    DELETE_ROLE: '/api/roles/:roleId',
    FETCH_ROLE_USER_PRIVILEGES: '/api/roles/checkPrivileges/:userPrivilege',

    PROFORMA_CALC: '/api/commands/proformaCalc/:companyCode/:period',
  },
  templates: {
    TRIAL_BALANCE: 'TLS_Trial_Balance_Template.xlsx',
    BATCH_TAX_ENTRIES: 'TLS_Balance_Sheet_Management_Tax_Entry.xlsx',
    BEGINNING_BALANCE_ES: 'TLS_Beginning_Balance_Template_ES.xlsx',
    LEGAL_ENTITY: 'TLS_Legal_Entity_Template.xlsx',
    TAX_ACCOUNTING: 'TLS_Tax_Accounting_Template.xlsx',
    BATCH_ADJUSTMENTS: 'TLS_Adjustments_Template.xlsx',
    TAX_RATES: 'TLS_Tax_Rates_Template.xlsx',
    BOOK_TO_TAX_DIFFERENCES: 'TLS_Book_To_Tax_Differences_Template.xlsx',
    BOOK_TO_TAX_CODE_DESCRIPTIONS: 'TLS_Book_to_Tax_Codes_Template.xlsx',
    CURRENCY_CONVERSION_RATES: 'TLS_Currency_Conversion_Rates_Template.xlsx',
    OBLIGATIONS: 'TLS_Obligations_Template.xlsx',
    COMPANY_CODES: 'TLS_Company_Codes_Template.xlsx',
    SL_ACCOUNTS: `TLS_Balance_Sheet_Management_Account_Template.xlsx`,
    GL_JOURNALS: 'TLS_Balance_Sheet_Management_GL_Journal_Template.xlsx',
    ADMIN_TAX_ENTRY: 'TLS_Balance_Sheet_Management_Admin_Tax_Entry.xlsx',
    CATEGORIES_AND_TAGS: 'TLS_Categories_and_Tags_Template.xlsx',
    TRIAL_BALANCE_THRESHOLD: 'TLS_Trial_Balance_Out_of_Balance_Threshold_Template.xlsx',
    GL_ACCOUNT_LIST: 'TLS_Balance_Sheet_Management_GL_Account_List_Template.xlsx',
    ACCOUNT_MAP: 'TLS_Balance_Sheet_Management_Account_Map_Template.xlsx',
    SCENARIOS: 'TLS_Scenario_Template.xlsx',
    TLS_ACCOUNTS: 'TLS_Accounts_Template.xlsx',
    USERS_AND_PRIVILEGES: 'TLS_Users_And_Privileges_Template.xlsx',
  },
  jimJobs: {
    trialBalanceImport: {
      type: 'trial-balance-import',
      displayName: 'Trial Balance Import',
      successMessage: 'Trial Balance File Upload Successful',
      errorMessage: 'Trial Balance File Upload Failed',
      progressBarText: 'Uploading file',
      page: Routes.provision.MAIN,
    },
    batchProvisionsImport: {
      displayName: 'Batch Provisions Import',
      type: 'batch-provision-import',
      successMessage: 'Batch Provisions successfully uploaded',
      errorMessage: 'Batch Provisions failed',
      page: Routes.provision.BATCH_PROVISIONS,
    },
    obligationsValidate: {
      displayName: 'Obligations Import Validation',
      type: 'obligations-validate',
      successMessage: 'Obligations file validation successfully',
      errorMessage: 'Obligations file validation failed',
      progressBarText: 'Validating',
      page: Routes.administration.OBLIGATIONS,
    },
    obligationsImport: {
      displayName: 'Obligations Import',
      type: 'obligations-import',
      successMessage: 'Obligations imported successfully',
      errorMessage: 'Obligations import failed',
      progressBarText: 'Importing',
      page: Routes.administration.OBLIGATIONS,
    },
    batchReportsExport: {
      displayName: ({ displayName }) => displayName,
      type: 'batch-export',
      successMessage: 'Batch report export completed successfully',
      errorMessage: 'Batch reports export failed',
      progressBarText: 'Generating Report',
      returnsFile: true,
    },
    batchArchiveReportsExport: {
      displayName: ({ displayName }) => displayName,
      type: 'archive-export',
      successMessage: 'Batch archive report completed successfully',
      errorMessage: 'Batch archive report export failed',
      progressBarText: 'Generating archive report',
      returnsFile: true,
    },
    customReportsExport: {
      displayName: ({ displayName }) => displayName,
      type: 'custom-reports-export',
      successMessage: 'Custom reports export completed successfully',
      errorMessage: 'Custom reports export failed',
      progressBarText: 'Exporting...',
      returnsFile: true,
    },
    tlsAccountsValidate: {
      displayName: 'TLS Accounts Import Validation',
      type: 'tls-accounts-validate',
      successMessage: 'TLS Accounts file uploaded successfully',
      errorMessage: 'TLS Accounts file upload failed',
      progressBarText: 'Validating',
      page: Routes.administration.TLS_ACCOUNTS,
    },
    tlsAccountsImport: {
      displayName: 'TLS Accounts Import',
      type: 'tls-accounts-import',
      successMessage: 'TLS Accounts imported successfully',
      errorMessage: 'TLS Accounts import failed',
      progressBarText: 'Importing',
      page: Routes.administration.TLS_ACCOUNTS,
    },
    automatedBatchCalc: {
      displayName: ({ displayName }) => displayName,
      type: 'automated-batch-calc',
      successMessage: 'Automated Batch Calc run successfully',
      errorMessage: 'JAutomated Batch Calc run failed',
      progressBarText: 'Generating...',
      returnsFile: true,
    },
    generateJournalFile: {
      displayName: ({ displayName }) => displayName,
      type: 'generate-journal-file',
      successMessage: 'Journal file generated successfully',
      errorMessage: 'Journal file generation failed',
      progressBarText: 'Generating journal file...',
      returnsFile: true,
    },
  },
  storageKeys: {
    redirectToAfterLogin: 'redirectToAfterLogin',
  },
  AUTH: {
    SIGN_OUT_URL: '/api/logoutRedirect',
    WINDOW_NAME: 'Sign Out',
  },
  BASE_PATH: ensureHasLeadingSlash(runtimeBasePath),
  CSRF_TOKEN_HEADER_NAME: 'csrf-token',
  CSRF_TOKEN_COOKIE_NAME: 'csrf-token',
  defaultToastrSettings: { removeOnHover: true, timeOut: 3000 },
  PENDO_KEY: runtimePendoKey,
};

export const jimJobConfigsByType = keyBy(Object.values(Config.jimJobs), 'type');
export default Config;
